import React from "react";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  makeStyles,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import { getDraftFormatLabel, safeDateFormat } from "../utils";

// Every property the table needs is pinned on the wrapper with descendant
// selectors: Analytics.css ships unscoped `table` / `th` / `td` rules (80%
// width, centred, 12px, column borders) that otherwise restyle this table.
const useStyles = makeStyles((theme) => ({
  wrap: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #E5E5E5",
    borderRadius: "6px",
    // Below the table's minimum width (a phone) it scrolls sideways instead of
    // cutting every column down to "…" and clipping the Edit/Download buttons.
    overflowX: "auto",
    "& table": {
      width: "100%",
      minWidth: 480,
      maxWidth: "100%",
      height: "auto",
      margin: 0,
      marginTop: 0,
      borderCollapse: "collapse",
      // Fixed layout keeps a long draft name from squeezing the other columns.
      tableLayout: "fixed",
      fontFamily: "inherit",
    },
    "& th, & td": {
      border: "none",
      padding: "8px 12px",
      fontSize: "0.85rem",
      color: TEXT.primary,
      verticalAlign: "middle",
      // Name and Draft Type wrap rather than truncate.
      whiteSpace: "normal",
      overflowWrap: "break-word",
    },
    // Headings, the date and the buttons stay on one line; their columns are
    // sized to fit them.
    "& th, & td:nth-child(3), & td:last-child": {
      whiteSpace: "nowrap",
    },
    "& thead th": {
      backgroundColor: "#F5F5F7",
      color: TEXT.strong,
      fontWeight: 600,
      fontSize: "0.75rem",
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      borderBottom: "1px solid #E5E5E5",
    },
    "& tbody tr + tr td": {
      borderTop: "1px solid #F0F0F0",
    },
    "& th:last-child, & td:last-child": {
      textAlign: "right",
    },
  },
  name: {
    fontWeight: 500,
  },
  editBtn: {
    textTransform: "none",
    minWidth: 0,
    padding: "2px 12px",
    marginRight: "4px",
    fontSize: "0.8rem",
  },
  iconBtn: {
    padding: "4px",
    color: TEXT.brand,
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
}));

/**
 * Order drafts of a case: name, draft type (format), last modified, and
 * download / edit actions. Shared by the admin Order Drafting tab and the
 * arbitrator's Record Order panel.
 *
 * `formats` is the list from /bac/arbitrator/order-drafts/formats/, used to
 * name each draft's type.
 */
export default function OrderDraftsTable({ drafts, formats, onDownload, onEdit }) {
  const classes = useStyles();

  return (
    <TableContainer className={classes.wrap}>
      <Table size="small" aria-label="Order drafts">
        {/* Modified On and Actions get the fixed width their one-line content
            needs (a date; Edit + the download icon). Name and Draft Type share
            what is left. */}
        <colgroup>
          <col />
          <col />
          <col style={{ width: 120 }} />
          <col style={{ width: 124 }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Draft Type</TableCell>
            <TableCell>Modified On</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {drafts.map((draft) => {
            const name = draft.title || `Draft #${draft.id}`;
            const type = getDraftFormatLabel(formats, draft.format);
            return (
              <TableRow key={draft.id}>
                <TableCell className={classes.name} title={name}>
                  {name}
                </TableCell>
                <TableCell title={type}>{type}</TableCell>
                <TableCell>
                  {safeDateFormat(draft.updated_at || draft.created_at)}
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    className={classes.editBtn}
                    onClick={() => onEdit(draft)}
                  >
                    Edit
                  </Button>
                  <IconButton
                    className={classes.iconBtn}
                    size="small"
                    title="Download as .docx"
                    onClick={() => onDownload(draft)}
                  >
                    <GetAppIcon style={{ fontSize: "1.1rem" }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
